import { useState, useEffect, useRef } from 'react';
import { Bot, Video, GraduationCap, Rocket, Crown, TrendingUp, Shield, Zap } from 'lucide-react';

const Counter = () => {
  const [count, setCount] = useState(0);
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = 3211;
    let animated = false;

    const easeOutCubic = (t: number, b: number, c: number, d: number) => {
      t /= d;
      t--;
      return c * (t * t * t + 1) + b;
    };

    const animateCounter = () => {
      let startTime: number | null = null;
      const duration = 1800;

      const frame = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const value = Math.min(Math.floor(easeOutCubic(elapsed, 0, target, duration)), target);
        setCount(value);
        if (elapsed < duration) {
          requestAnimationFrame(frame);
        } else {
          setCount(target);
          setInterval(() => {
            setCount(prev => prev + Math.floor(Math.random() * 3));
          }, 3000);
        }
      };
      requestAnimationFrame(frame);
    };

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !animated) {
          animateCounter();
          animated = true;
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={counterRef}
      className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 py-2 px-4 sm:py-3 sm:px-6 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-2 border-emerald-400/20 text-emerald-100 font-semibold backdrop-blur-sm text-sm sm:text-base"
    >
      <TrendingUp className="text-emerald-400" size={18} />
      <span>Over</span>
      <strong className="bg-emerald-400 text-slate-900 py-1 px-3 sm:py-1.5 sm:px-4 rounded-full font-black text-sm sm:text-base">
        {count.toLocaleString()}
      </strong>
      <span>traders joined!</span>
    </div>
  );
};

const ServiceCard = ({
  icon: Icon,
  title,
  titleAr,
  description,
  features,
  price,
  priceAr,
  ctaText,
  ctaLink,
  delay,
}: {
  icon: React.ElementType;
  title: string;
  titleAr: string;
  description: string;
  features: string[];
  price: string;
  priceAr: string;
  ctaText: string;
  ctaLink: string;
  delay: number;
}) => {
  return (
    <div
      className="group relative bg-gradient-to-br from-slate-900/90 to-slate-800/90 backdrop-blur-xl border border-emerald-400/20 rounded-2xl sm:rounded-3xl p-5 sm:p-8 transition-all duration-500 hover:scale-105 hover:border-emerald-400/60 hover:shadow-[0_0_60px_rgba(52,211,153,0.3)]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/0 via-emerald-400/5 to-teal-400/0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-emerald-400/50 flex-shrink-0">
          <Icon className="text-slate-900" size={32} strokeWidth={2.5} />
        </div>

        <h3 className="text-xl sm:text-2xl font-black text-white mb-1 sm:mb-2">{title}</h3>
        <h4 className="text-lg sm:text-xl font-bold text-emerald-300 mb-3 sm:mb-4">{titleAr}</h4>

        <p className="text-slate-300 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">{description}</p>

        <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-slate-300 text-xs sm:text-sm">
              <Shield className="text-emerald-400 flex-shrink-0 mt-0.5" size={14} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-baseline gap-2 mb-4 sm:mb-6">
          <span className="text-3xl sm:text-4xl font-black text-emerald-400">{price}</span>
          <span className="text-base sm:text-lg text-slate-400">{priceAr}</span>
        </div>

        <a
          href={ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 font-black text-center uppercase tracking-wider text-xs sm:text-sm shadow-lg shadow-emerald-400/30 hover:shadow-xl hover:shadow-emerald-400/50 transition-all duration-300 hover:scale-105"
        >
          {ctaText}
        </a>
      </div>
    </div>
  );
};

const StatsCard = ({ icon: Icon, value, label }: { icon: React.ElementType; value: string; label: string }) => {
  return (
    <div className="flex items-center gap-3 sm:gap-4 bg-slate-900/50 backdrop-blur-sm border border-emerald-400/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-emerald-400/40 transition-all duration-300">
      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-400/20 to-teal-500/20 flex items-center justify-center flex-shrink-0">
        <Icon className="text-emerald-400" size={24} />
      </div>
      <div className="min-w-0">
        <div className="text-xl sm:text-2xl font-black text-white">{value}</div>
        <div className="text-xs sm:text-sm text-slate-400">{label}</div>
      </div>
    </div>
  );
};

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(52,211,153,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(20,184,166,0.1),transparent_50%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <header className="text-center mb-12 sm:mb-20">
          <div className="inline-flex flex-wrap items-center justify-center gap-1 sm:gap-2 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-3 sm:px-5 py-1.5 sm:py-2 mb-6 sm:mb-8 backdrop-blur-sm text-xs sm:text-sm">
            <Zap className="text-emerald-400" size={16} />
            <span className="text-emerald-300 font-semibold">انضم وكن واحد من نخبه المتداولين</span>
          </div>

<p
  style={{ fontFamily: 'Nitro Oplique, sans-serif' }}
  className="text-base sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed px-2"
>
  لا تعمل من أجل المال، بل اجعل المال يعمل لأجلك
</p>


<div className="flex flex-col items-center gap-[6px] sm:gap-[8px] mb-8 sm:mb-12 mt-8 sm:mt-10">
  {/* كلمة JOIN NOW */}
  <p className="text-lg sm:text-2xl text-emerald-400 font-extrabold tracking-widest animate-pulse drop-shadow-[0_0_10px_#22ff9b]">
    JOIN NOW
  </p>

  {/* زر الانضمام للتليجرام */}
  <a
    className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 py-3 sm:py-4 px-5 sm:px-8 rounded-lg sm:rounded-xl font-black uppercase tracking-wider text-xs sm:text-sm shadow-lg shadow-emerald-400/30 hover:shadow-xl hover:shadow-emerald-400/50 transition-all duration-300 hover:scale-105"
    href="https://t.me/ALPASHMO7ASB_TEAM"
    target="_blank"
    rel="noopener noreferrer"
  >
    <Rocket size={20} />
    <span>Join My Telegram Channel</span>
  </a>

  {/* العداد */}
  <div className="mt-1 sm:mt-2">
    <Counter />
  </div>
</div>






          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-12 sm:mb-16">
            <StatsCard icon={Crown} value="5+ Years" label="خبره بالتداول" />
            <StatsCard icon={TrendingUp} value="3200+" label="أعضاء نشطين" />
            <StatsCard icon={Shield} value="100%" label="نتائج موثوقة" />
          </div>
        </header>

        <section className="mb-12 sm:mb-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              Premium Services
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent mx-auto mb-3 sm:mb-4 rounded-full" />
            <p className="text-slate-400 text-sm sm:text-lg">اختر الخدمه التي تناسبك</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
<ServiceCard
  icon={Bot}
  title="AI Trading Robot"
  titleAr="روبوت الصفقات الالي"
  description="روبوت تداول ذكي متقدم بتحليل السوق بشكل لحظي وإنشاء إشارات تلقائية"
  features={[
    'ربح مباشر بدون مضاعفات',
    'نسبة ربح تتراوح بين 85% - 90%',
    'دعم فني متواجد 24 ساعة',
    'اشعارات مخصصه لكل صفقه',
    'يصلك يوميا 300 صفقه',
    'إعدادات سهلة وسريعة',
    'تحليل فني دقيق باحدث استراتيجيات الـ AI',
  ]}
  price="$35"
  priceAr="سعر مميز"
  ctaText="تحميل الروبوت"
  ctaLink="https://www.mediafire.com/file/j606pjg80ddxmuh/Signal-X.apk/file"
  delay={0}
/>

<ServiceCard
  icon={Video}
  title="Live Trading"
  titleAr="جلسات التداول المباشرة"
  description="انضم لجلسات تداول حية حصرية مع تحليل السوق المباشر واستراتيجيات احترافية"
  features={[
    'دفع واستلام الارباح بسهوله',
    'جلسات يوميه باستمرار',
    'نظام تكبير رأس مال قوي',
    'ضمان عدم الخساره وتحمل المسؤوليه',
    'بدايه بأقل راس مال 15$',
  ]}
  price="$***"
  priceAr="نسبتنا 40% فقط"
  ctaText="انضم الان"
  ctaLink="https://t.me/m/92P7MxawM2Rk"
  delay={200}
/>

<ServiceCard
  icon={GraduationCap}
  title="Premium Course trade"
  titleAr="كورس التداول المتكامل"
  description="كورس تداول شامل من المبتدئين إلى الخبراء مع استراتيجيات وتقنيات مثبتة"
  features={[
    'شرح متكامل من الألف إلى الياء',
    'دروس فيديو وملفات PDF تعليمية',
    'إتقان التحكم في النفس ',
    'تعلم اداره الحساب والمخاطر',
    'دخول مدى الحياة وتحديثات مستمرة',
    'استراتيجيات مدفوعه',
  ]}
  price="$15"
  priceAr="دفعة مرة واحدة"
  ctaText="اشترك الآن"
  ctaLink="https://t.me/m/ipt7KszyNjU0"
  delay={400}
/>

          </div>
        </section>

        <section className="mb-12 sm:mb-20">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-xl border border-emerald-400/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8">
              <div className="flex-shrink-0">
                <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full border-4 border-emerald-400/30 overflow-hidden shadow-xl shadow-emerald-400/20">
                  <img
                    src="/Tarek.jpg"
                    alt="AL PASHMO7ASB"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/200x200/1e293b/34d399?text=AL+PASHMO7ASB';
                    }}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h2 className="text-2xl sm:text-3xl font-black text-emerald-400 mb-3 sm:mb-4 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <Shield size={28} />
                  <span>I'm AL PASHMO7ASB</span>
                  <Crown size={24} className="text-yellow-400" />
                </h2>
                <p className="text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-3 sm:mb-4">
                  I started from nothing — no shortcuts, just discipline and a deep hunger to grow. With 5+ years in
                  Binary Options, Crypto and Programming, I blend strategies with technology to deliver consistent
                  results.
                </p>
                <p className="text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 sm:mb-6">
                  I don't just teach trading — I build traders.
                </p>
                <div className="bg-gradient-to-r from-emerald-400/10 to-teal-400/10 border border-emerald-400/30 rounded-lg sm:rounded-xl p-4 sm:p-6 backdrop-blur-sm">
                  <p className="text-emerald-300 font-bold text-base sm:text-lg lg:text-xl text-center italic">
                    "I don't just teach trading. I build traders."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="text-center py-8 sm:py-12">
          <div className="flex gap-2 sm:gap-4 justify-center mb-6 sm:mb-8 flex-wrap">
            <a
              className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900/50 border border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10 hover:border-emerald-400/60 transition-all duration-300 hover:scale-110"
              href="https://t.me/ALPASHMO7ASB"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact on Telegram"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
              </svg>
            </a>
            <a
              className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900/50 border border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10 hover:border-emerald-400/60 transition-all duration-300 hover:scale-110"
              href="https://www.instagram.com/alpashmo7asb"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow on Instagram"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a
              className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900/50 border border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10 hover:border-emerald-400/60 transition-all duration-300 hover:scale-110"
              href="https://www.youtube.com/@ALPASHMO7ASB"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Subscribe on YouTube"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </a>
          </div>

          <p className="text-slate-500 text-xs sm:text-sm">
            © 2025 AL PASHMO7ASB. All rights reserved. Trade responsibly.
          </p>
        </footer>
      </div>
    </div>
  );
}
